import React, { FC, useEffect, useRef, useState } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { Spinner, Box, theme } from '@bechtel/gds';
import { MapComponentProps, MapProps, MarkerClustererExt, MarkerExt } from './types';
import { Trans } from '@lingui/macro';
import { filter, includes, map, uniqBy } from 'lodash';
import { MarkerType } from './types';
import { MapFilters } from './filters';
import { MapFilterStyled } from './styled';
import ReactDOMServer from 'react-dom/server';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { appRender } from '@bechtel/client-common';

export const Map: FC<MapProps> = ({
  apiKey,
  center,
  mapTypeId,
  markers,
  onMapClick,
  zoom = 16,
  hasFilter = false,
  onFilterChange,
  selectedMarkerTypes,
  setSelectedMarkerTypes,
  markerAllTypes,
}) => {
  const [allMarkers, setAllMarkers] = useState<MarkerExt[]>();
  const [selectedMarkers, setSelectedMarkers] = useState<MarkerExt[]>();
  const [googleMap, setGoogleMap] = useState<google.maps.Map>();
  const [markerClusterers, setMarkerClusterers] = useState<MarkerClustererExt[]>();
  const markerTypes: MarkerType[] = map(uniqBy(markers, 'markerType'), 'markerType');

  const clusterRender = (count, position, color) => {
    return new google.maps.Marker({
      label: {
        text: String(count),
        color: theme.colors.white,
        fontWeight: String(theme.fontWeights[500]),
        fontSize: String(theme.fontSizes[400]),
      },
      position,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: color,
        fillOpacity: 0.86,
        strokeColor: 'white',
        strokeWeight: 1,
      },
    });
  };

  const pipeClusterRenderer = {
    render({ count, position }) {
      // TODO:change color here since it is not there in gds token
      return clusterRender(count, position, '#0b81c3');
    },
  };

  const steelClusterRenderer = {
    render({ count, position }) {
      return clusterRender(count, position, String(theme.colors.red[700]));
    },
  };

  const dataVaultClusterRenderer = {
    render({ count, position }) {
      return clusterRender(count, position, String(theme.colors.grey[700]));
    },
  };

  const rodRoomClusterRenderer = {
    render({ count, position }) {
      return clusterRender(count, position, String(theme.colors.black));
    },
  };

  useEffect(() => {
    if (!googleMap) return;
    const markerClusters = map(markerTypes, (item) => {
      const markerClusterer: MarkerClustererExt = {
        type: item,
        markerClusterer: new MarkerClusterer({
          map: googleMap,
          renderer:
            item === MarkerType.PIPING
              ? pipeClusterRenderer
              : item === MarkerType.STEEL
              ? steelClusterRenderer
              : item === MarkerType.DATA_VAULT
              ? dataVaultClusterRenderer
              : item === MarkerType.ROD_ROOM
              ? rodRoomClusterRenderer
              : rodRoomClusterRenderer,
        }),
      };
      return markerClusterer;
    });
    setMarkerClusterers(markerClusters);
  }, [googleMap]);

  useEffect(() => {
    if (!googleMap || markers.length === 0) return;
    const infoWindow = new google.maps.InfoWindow({
      content: '',
    });
    const markerList: MarkerExt[] = markers.map((item) => {
      const marker = new google.maps.Marker({
        position: { lat: Number(item.position.lat), lng: Number(item.position.lng) },
        icon: item.icon,
        map: googleMap,
      });

      marker.addListener('click', () => {
        if (item.infoWindowContent) {
          const view = appRender(item.infoWindowContent);
          infoWindow.setContent(ReactDOMServer.renderToString(view));
          infoWindow.open({ anchor: marker });
        }

        if (item.onClick) item.onClick(item.id, marker);
      });
      const markerExt: MarkerExt = { marker, type: item.markerType };
      return markerExt;
    });
    setAllMarkers(markerList);
    return () => {
      markerList.forEach((marker) => {
        marker.marker.setMap(null);
      });
    };
  }, [googleMap, markers]);

  useEffect(() => {
    if (!allMarkers || allMarkers.length === 0) return;
    setSelectedMarkers(allMarkers);
  }, [allMarkers]);

  useEffect(() => {
    if (!googleMap || !selectedMarkers || selectedMarkers.length === 0) return;

    if (allMarkers && allMarkers.length > 0)
      allMarkers.forEach((item) => {
        item.marker.setMap(null);
      });
    selectedMarkers.forEach((item) => {
      item.marker.setMap(googleMap);
    });
    if (markerClusterers && markerClusterers.length > 0) {
      markerClusterers.forEach((item) => {
        item.markerClusterer.clearMarkers();
        const typedMarkers = filter(selectedMarkers, (markerItem) => {
          return item.type == markerItem.type;
        });
        item.markerClusterer.addMarkers(map(typedMarkers, 'marker'));
      });
    }
    // Add a marker clusterer to manage the markers.
  }, [googleMap, selectedMarkers]);

  useEffect(() => {
    if (!hasFilter) return;
    if (selectedMarkerTypes.length === 0) {
      setSelectedMarkers(allMarkers);
      return;
    }
    const filteredMarkers = filter(allMarkers, (item: MarkerExt) => {
      return includes(selectedMarkerTypes, item.type);
    });
    setSelectedMarkers(filteredMarkers);
  }, [hasFilter, selectedMarkerTypes]);

  return (
    <Box flexGrow={{ sm: 1 }} style={{ position: 'relative' }}>
      <Wrapper apiKey={apiKey} render={(status) => render(status)}>
        <MapComponent
          map={googleMap}
          setMap={setGoogleMap}
          center={center}
          zoom={zoom}
          mapTypeId={mapTypeId}
          onMapClick={onMapClick}
        />
      </Wrapper>
      {hasFilter && (
        <MapFilterStyled>
          <MapFilters
            markerTypes={markerAllTypes}
            onFilterChange={onFilterChange}
            selectedMarkerTypes={selectedMarkerTypes}
            setSelectedMarkerTypes={setSelectedMarkerTypes}
          />
        </MapFilterStyled>
      )}
    </Box>
  );
};

const render = (status: Status) => {
  switch (status) {
    case Status.LOADING:
      return <Spinner />;
    case Status.FAILURE:
      return <Trans>An error has occured loading the map. Please refresh the browser and try again.</Trans>;
    default:
      return <></>;
  }
};

const MapComponent: FC<MapComponentProps> = ({ map, setMap, center, zoom, mapTypeId, onMapClick }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current && !map) {
      setMap(new window.google.maps.Map(ref.current, { center, zoom, mapTypeId }));
    }
  }, [ref, map, center, zoom, mapTypeId, setMap]);

  useEffect(() => {
    if (!map || !onMapClick) return;
    google.maps.event.addListener(map, 'click', (event: google.maps.MapMouseEvent) => {
      onMapClick(event.latLng);
    });
  }, [map, onMapClick]);

  return <div ref={ref} id="map" style={{ width: '100%' }} />;
};
